import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'status';

@Component({
  selector: 'app-custom-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './custom-modal.component.html',
  styleUrl: './custom-modal.component.scss'
})
export class CustomModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() title: string = 'Modal Title';
  @Input() size: ModalSize = 'md';
  @Input() isOpen: boolean = false;
  @Input() closable: boolean = true;
  @Input() backdropClose: boolean = true;
  @Input() hasFooterContent: boolean = false;
  @Input() hasHeaderContent: boolean = false;

  @Output() modalClose = new EventEmitter<void>();
  @Output() modalOpen = new EventEmitter<void>();

  @ViewChild('modalContainer', { static: false }) modalContainer!: ElementRef<HTMLDivElement>;

  // Removed resizing properties - expand functionality disabled

  private originalParent: Node | null = null;
  private originalNextSibling: Node | null = null;
  private movedToBody = false;

  // Size configurations - using auto height for better content fit
  private sizeConfig = {
    sm: { width: 300, minHeight: 200 },
    md: { width: 500, minHeight: 300 },
    lg: { width: 600, minHeight: 400 },
    xl: { width: 800, minHeight: 500 },
    xxl: { width: 1000, minHeight: 600 },
    status: { width: 356, minHeight: 423 }
  };

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    if (this.isOpen) {
      this.open();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && !changes['isOpen'].firstChange) {
      if (this.isOpen) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  ngOnDestroy(): void {
    // When the component is destroyed while portaled to <body>, remove the host
    // from the live DOM instead of restoring it into a view Angular is tearing down.
    document.body.style.overflow = '';
    this.detachFromCurrentParent();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isOpen && this.closable) {
      this.close();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    if (this.isOpen && this.modalContainer) {
      // Ensure modal stays within viewport bounds on window resize
      setTimeout(() => {
        this.constrainModalToViewport();
      });
    }
  }

  open(): void {
    this.isOpen = true;
    this.modalOpen.emit();

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    this.attachToBody();

    // Set initial size based on size input - using auto height
    setTimeout(() => {
      if (this.modalContainer) {
        const config = this.sizeConfig[this.size];
        const modalEl = this.modalContainer.nativeElement;
        modalEl.style.width = `${config.width}px`;
        modalEl.style.minHeight = `${config.minHeight}px`;
        modalEl.style.height = 'auto';
        this.centerModal();
      }
    });
  }

  close(): void {
    if (!this.closable) return;
    
    this.isOpen = false;
    this.modalClose.emit();
    
    // Restore body scroll
    document.body.style.overflow = '';
    this.restoreToOriginalParent();
  }

  onBackdropClick(event: MouseEvent): void {
    // Close modal when clicking on backdrop
    if (this.backdropClose && this.closable && event.target === event.currentTarget) {
      this.close();
    }
  }

  private centerModal(): void {
    if (!this.modalContainer) return;
    
    // CSS flexbox on the backdrop handles centering automatically.
    // Clear any stale inline position so the flex layout takes effect.
    const modalEl = this.modalContainer.nativeElement;
    modalEl.style.left = '';
    modalEl.style.top = '';
  }

  private constrainModalToViewport(): void {
    // CSS flexbox on the backdrop keeps the modal centered on resize.
    // Just clear any stale inline positioning.
    this.centerModal();
  }

  private attachToBody(): void {
    if (this.movedToBody) return;
    const host = this.elementRef.nativeElement;
    this.originalParent = host.parentNode;
    this.originalNextSibling = host.nextSibling;
    document.body.appendChild(host);
    this.movedToBody = true;
  }

  private restoreToOriginalParent(): void {
    if (!this.movedToBody || !this.originalParent) return;
    const host = this.elementRef.nativeElement;
    if (this.originalNextSibling && this.originalParent.contains(this.originalNextSibling)) {
      this.originalParent.insertBefore(host, this.originalNextSibling);
    } else {
      this.originalParent.appendChild(host);
    }
    this.movedToBody = false;
  }

  private detachFromCurrentParent(): void {
    if (!this.movedToBody) return;
    const host = this.elementRef.nativeElement;
    host.parentNode?.removeChild(host);
    this.movedToBody = false;
    this.originalParent = null;
    this.originalNextSibling = null;
  }

} 
